import { useQuery } from '@tanstack/react-query'
import { api, queryKeys } from './api'
import type { Color, DraftShortage, InventoryItem, InventoryWarning, ProjectListItem, Purchase, ToolRecord, TopConsumed, RecentCalibration } from './models'

export const useColors = () => useQuery({ queryKey: queryKeys.colors, queryFn: () => api<Color[]>('/api/colors'), staleTime: 10 * 60_000 })
export const useInventory = () => useQuery({ queryKey: queryKeys.inventory, queryFn: () => api<InventoryItem[]>('/api/inventory') })
export const useProjects = () => useQuery({ queryKey: queryKeys.projects, queryFn: () => api<ProjectListItem[]>('/api/projects') })
export const usePurchases = () => useQuery({ queryKey: queryKeys.purchases, queryFn: () => api<Purchase[]>('/api/purchases') })
export const useTools = () => useQuery({ queryKey: queryKeys.tools, queryFn: () => api<ToolRecord[]>('/api/tools') })
export const useShortages = () => useQuery({ queryKey: queryKeys.shortages, queryFn: () => api<DraftShortage[]>('/api/stats/draft-shortage') })
export const useWarnings = () => useQuery({ queryKey: queryKeys.warnings, queryFn: () => api<InventoryWarning[]>('/api/stats/inventory-warning') })
export const useTopConsumed = (by: 'freq' | 'total') => useQuery({ queryKey: queryKeys.topConsumed(by), queryFn: () => api<TopConsumed[]>(`/api/stats/top-consumed?by=${by}`) })
export const useCalibrations = () => useQuery({ queryKey: queryKeys.calibrations, queryFn: () => api<RecentCalibration[]>('/api/inventory/calibrations/recent') })

export function invalidateAll(queryClient: import('@tanstack/react-query').QueryClient): Promise<void> {
  return queryClient.invalidateQueries().then(() => undefined)
}

export function plannedNeed(projects: ProjectListItem[]): Map<string, number> {
  const result = new Map<string, number>()
  for (const project of projects) {
    if (project.status !== 'planned') continue
    for (const item of project.consumptions) {
      const code = item.actual_color_code || item.color_code
      result.set(code, (result.get(code) ?? 0) + Number(item.quantity || 0))
    }
  }
  return result
}

export function projectDisplayStatus(project: ProjectListItem): 'inbox' | 'planned' | 'done' {
  return project.status
}
